import React, { useCallback, useEffect, useRef, useState } from 'react'

// Libs
import {
  startOfDay,
  addHours,
  format,
  startOfMonth,
  endOfMonth,
} from 'date-fns'
import pt from 'date-fns/locale/pt'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useSelector, useDispatch } from 'react-redux'
import ReactPlayer from 'react-player'
import Joyride, { STATUS } from 'react-joyride'
import { Helmet } from 'react-helmet-async'
import { useHistory } from 'react-router'

// Redux
import * as actions from '../../redux/actions'
import * as filterHomepageActions from '../../redux/modules/filterHomepage/actions'
import { homepageSkillsRequest } from '../../redux/modules/filterHomepage/requests'

// Components
import ProfessionalsNetwork from 'components/ProfessionalsNetwork/ProfessionalsNetwork'
import { ProfessionalCardLoading } from 'components/ProfessionalCard/ProfessionalCard'
import { Typography, useDimensions, Avatar, Select, Button } from '@telavita-core/react-design-kit'
import ProfessionalCard from '../../components/ProfessionalCard'
import ModalGeneric from '../../containers/ModalManager/Modals/ModalGeneric'
import { Column, Container, CreditsPanel, TimestampTag } from 'components'
import ResultNotFound from '../../components/ResultNotFound'
import FilterHeader from '../../components/FilterHeader'
import SignInBar from 'components/SignInBar'
import Footer from '../../components/Footer'
import Row from '../../components/Row'
import Tooltip from 'components/Tooltip'
import { Review, ReviewLoading, ListContainer } from 'components/Review'
import BallonCommentIcon from 'components/CoreIcons/Icons/BallonComment'
import { TherapyApproachModal } from 'containers/ModalManager/Modals/TherapyApproachModal'
import { MatchProfessionalsModal } from 'containers/ModalManager/Modals/MatchProfessionalsModal'

// Utils/Services
import { getProfessionalAvailability } from 'services/professionalAvailabilityService'
import { reviewsOptions } from 'settings/_reviewsOptionsSettings'
import { INACTIVE } from 'settings/_personSettings'
import { getRoleFromProfile } from 'utils/textTransformations'
import disableScroll from 'utils/disableScroll'
import ProfessionalsService from 'services/professionals'

// Hooks
import { featureName, useUnleashFlag } from 'modules/FeatureFlags/Unleash'

// Assets
import ProfFakeImage from 'static/images/prof-fake.jpg'
import { ReactComponent as MatchShowcaseIcon } from '../../static/svg/match-showcase-icon.svg'
import { ReactComponent as CloseModalIcon } from '../../static/svg/close-modal.svg'
import { updatePatientDefaultPlan } from 'services/updatePatientDefaultPlan'
import { AUTH_UPDATE_DEFAULT_PLAN, CLEAR_PERSONS } from 'redux/actions/_actionTypes'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { QuickScheduleProvider } from './context/QuickScheduleContext'
import { usePatientEligibility } from 'hooks/usePatientEligibility'
import { BlockScheduleModal } from 'containers/ModalManager/Modals/BlockScheduleModal'

import * as routes from 'settings/_routesSettings'
import { useGetPatientSchedules } from 'hooks/useGetPatientSchedules'
import { searchQueryAvailabilitiesDate } from 'utils/availabilitiesSearchHandler'
import { useUpdateProfileSelectedPlansLocally, useEnablePsychiatry, usePlanGroupAllowsReservation } from 'hooks'
import { TVTVPACI } from 'settings/_profileSettings'
import { PATIENT_SAVED_PLAN } from 'settings/_storageFlagsSettings'

const {
  VITTA
} = PlanGroupCategoryEnum

function nineHoursRuleHandler(homepageType) {
  /* 
    Regra:
    Se o usuário visitar a vitrine de psicologia antes das 9h da manhã, 
    a busca de horários dever ser feita com o dia atual.

    Se o usuário visitar a vitrine de psicologia após as 9h da manhã, 
    a busca de horários dever ser feita com o dia seguinte (a partir de 00h do dia seguinte)

    Importante: Essa regra vale apenas para psicologia
 */

  const isPsychiatristHomepage = homepageType === 'PSIQ'
  
  if (isPsychiatristHomepage) return new Date()

  const isNowBeforeNineHours = new Date().getHours() < 9

  return isNowBeforeNineHours ? new Date() : addHours(new Date(), 24)
}

const Homepage = ({ config }) => {
  const {
    hasButton,
    setHasButton,
    planGroupName,
    creditsStatus,
    maxCredits,
    enableDetails,
    validDate,
    expired,
    errorButton,
    loadingButton,
    solicitationDate,
    isUnlimited,
    startDate,
    corporateEligibility,
    loading: loadingEligibility,
    forceUpdate,
    psychiatryEligibilityLoading,
    psychiatryEligibility,
    isCareplus,
    isCassi,
    isCapitation,
    planGroupCode,
    onClickRequestCreditsButton,
    handleRequestTreatementStatus,
    reasonCode,
    creditsPanelErrorMessage
  } = usePatientEligibility(true)

  const { showFeature: matchProfessionalFeature } = useUnleashFlag(featureName.MATCH_PROFESSIONAL_SHOWCASE)

  const dispatch = useDispatch()
  const { isMobile } = useDimensions()

  const firstUpdate = useRef(true)

  const isPsychologist = config.type === 'PSIC'
  const isPsychiatrist = config.type === 'PSIQ'
  const showMatchProfessionalFeature = matchProfessionalFeature && isPsychologist

  const [isShowingVideo, setIsShowingVideo] = useState(false)
  const [isMatchProfessional, setIsMatchProfessional] = useState(false)
  const [selectedProfessional, setSelectedProfessional] = useState({})
  const { updateProfileSelectedPlansLocally } = useUpdateProfileSelectedPlansLocally()

  const matchProfessionalHandled = () => setIsMatchProfessional(true)
  const resetMatchProfessional = () => setIsMatchProfessional(false)

  const steps = [
    {
      target: '.FilterHeader__ExperiencesFilter',
      content:
        <><span>Novidade</span>: Está mais fácil filtrar profissionais por tema. É só selecionar um da lista ou buscar.</>,
      disableBeacon: true,
      offset: 0,
      disableCloseOnEsc: true,
      disableOverlayClose: true,
    },
    {
      target: '.FilterHeader__DayFilter',
      content:
        'Agora, por padrão, já trazemos o próximo dia com os primeiros horários disponíveis. Para definir outra data, é só clicar.',
      offset: 0,
      disableCloseOnEsc: true,
      disableOverlayClose: true,
    },
    {
      target: '.ProfessionalCard__information__tabContainer__labelContainer',
      content:
        'Consulte mais rápidamente as experiências e a formação do profissional.',
      offset: 0,
      placement: 'bottom-start',
      disableCloseOnEsc: true,
      disableOverlayClose: true,
    },
    {
      target: '.ProfessionalCard__availabilities__slots',
      content:
        'Verifique os horários disponíveis do profissional sem ter que abrir o perfil completo dele.',
      offset: 0,
      disableCloseOnEsc: true,
      disableOverlayClose: true,
    },
    {
      target:
        '.ProfessionalCard__availabilities__controlsContainer__controls button:nth-child(2)',
      content:
        'Para ver horários dos próximos dias apenas deste profissional, é só clicar em avançar.',
      offset: 0,
      placement: 'bottom-end',
      disableCloseOnEsc: true,
      disableOverlayClicks: true,
    },
  ]

  const history = useHistory()
  const selectedDay = useSelector((state) => state.filters.day)
  const failMessage = useSelector((state) => state.auth.failMessage)
  const isAuth = useSelector((state) => state.auth.isAuth)
  const patientPlans = useSelector((state) => state?.auth?.profileSelected?.plans.filter(plan => plan.status_code !== INACTIVE && plan.plan_group.category !== VITTA))
  const planSelected = useSelector((state) => state?.auth?.profileSelected?.plans.find(pPlan => pPlan.default === true))
  const isParticular = planSelected === undefined ? true : planSelected.partner_plan_is_particular
  const persons = useSelector((state) => state.persons.persons)
  const selectedStatus = useSelector((state) => state.filters.gender_status)
  const userData = useSelector((state) => state.auth.userData)
  const userProfileCode = userData?.person_profile[0]?.profile_code
  const loading = useSelector((state) => state.filtersHomepage.loading)
  const paginationData = useSelector(
    (state) => state.filtersHomepage.pagination
  )
  const errorList = useSelector((state) => state.async.errorList)
  const actualTheme = useSelector((state) => state.filtersHomepage.actualTheme)
  const actualSubject = useSelector(
    (state) => state.filtersHomepage.actualSubject
  )
  const actualApproach = useSelector(
    (state) => state.filtersHomepage.actualApproach
  )
  const actualProfessionalNameTerm = useSelector(
    (state) => state.filtersHomepage.professionalNameTerm
  )
  const actualAgeRanges = useSelector(
    (state) => state.filtersHomepage.ageRanges
  )
  const actualLanguages = useSelector(
    (state) => state.filtersHomepage.languages
  )

  const approaches = useSelector((state) => state.filtersHomepage.approaches)
  const skills = useSelector((state) => state.filtersHomepage.skills)

  const professionalsNotFound = errorList?.feedbackError?.some(
    (err) => err.info === 'professional_not_found'
  )

  const planName = planSelected?.partner_plan_name
  const planGroupCategory = planSelected?.plan_group.category
  const currentFetchDate = selectedDay
    ? new Date(selectedDay)
    : nineHoursRuleHandler(config.type)

  const [approachesModal, setApproachesModal] = useState(false)
  const [reviewsModal, setReviewsModal] = useState(false)
  const [matchProfessionalsModal, setMatchProfessionalsModal] = useState(false)
  const [selectedReviewOption, setSelectedReviewOption] = useState(() => reviewsOptions.find(
    (review) => review.id === 1
  ))
  const [reviews, setReviews] = useState([])
  const [loadingReviews, setLoadingReviews] = useState(false)
  const [pageReviews, setPageReviews] = useState(0)
  const [totalReviews, setTotalReviews] = useState(0)
  const [isOnboarding, setIsOnboarding] = useState(() => {
    const onboarding = !isMobile
      ? localStorage.getItem('telavitaCompletedOnboarding')
      : false
    return !onboarding
  })

  const [patientAlreadyHasReservation, setPatientAlreadyHasReservation] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState(currentFetchDate)
  const isLoggedUserPatient = userProfileCode === TVTVPACI

  const planGroupAllowsReservation = usePlanGroupAllowsReservation()

  const { patientSchedulesForDayPicker, isLoadingPatientSchedules } = useGetPatientSchedules({
    patientUsername: userData?.username,
    startDate: startOfMonth(selectedMonth)?.toISOString(),
    endDate: endOfMonth(selectedMonth)?.toISOString(),
    shouldRequest: !!isAuth && !!userData?.username,
    scheduleType: config.type
  })
  
  const trackFilterEvent = () => {
    /*
     * Envia um evento de filtragem para o Google Analytics.
     */

    if (window.Indicative) {
      window.Indicative.buildEvent('Vitrine', {
        Resultados: paginationData.total,
        ...(selectedDay && {
          'Data buscada': new Date(selectedDay).toISOString(),
        }),
        ...(planSelected && {
          Plano: planSelected.partner_plan_name,
          ...(planSelected.plan_group && {
            Convênio: planSelected.plan_group.name,
          }),
        }),
        ...(actualTheme && { Tema: actualTheme }),
        ...(actualSubject && { Assunto: actualSubject }),
        ...(actualApproach && { Abordagem: actualApproach }),
        ...(selectedStatus && { Gênero: selectedStatus }),
      })
    }
  }

  const onCounterMoreFilters = useCallback(() => {
    let counter = 0

    const conditionsToIncrementFilterCounter = [
      selectedStatus && selectedStatus !== 'ALL',
      actualProfessionalNameTerm,
      actualApproach,
      actualAgeRanges.length > 0,
      actualLanguages.length > 0,
    ]

    conditionsToIncrementFilterCounter.forEach((condition) => {
      if (condition) {
        counter = counter + 1
      }
    })

    return counter
  }, [actualApproach, actualProfessionalNameTerm, selectedStatus, actualAgeRanges, actualLanguages])

  const onChangeFilterCriteria = (planCode) => {
    dispatch(filterHomepageActions.clearPersons())
    dispatch(actions.clearPersons())
    handleScrollTop()
    onCounterMoreFilters()
    loadProfessionals({
      page: 1,
      planCode
    })
  }

  const createAvailabilities = ({ startDate, endDate }) => {
    let availabilities = [{
      startDate: startOfDay(new Date(startDate)),
      endDate: addHours(startOfDay(new Date(endDate ? endDate : startDate)), 1)
    }]

    for (let i = 0; i < 20; i++) {
      availabilities.push({
        startDate: addHours(availabilities[i].startDate, 1),
        endDate: addHours(availabilities[i].endDate, 1)
      })
    }

    return [
      {
        options: availabilities,
      },
    ]
  }

  const handleJoyrideCallback = (data) => {
    if ([STATUS.FINISHED].includes(data.status)) {
      setIsOnboarding(false)
      localStorage.setItem('telavitaCompletedOnboarding', true)
      disableScroll.off()
    }
  }

  const handleScrollTop = () => window.scrollTo(0, 0)

  const loadNextAvailabilities = async ({
    slug,
    profileRole,
    startDate,
    endDate,
    productCode,
  }) => {
    const res = await getProfessionalAvailability({
      slug,
      profileRole,
      startDate,
      endDate,
      planCode: planSelected?.partner_plan_code,
      isAuthorized: isAuth && isPsychologist,
      username: userData?.username && isPsychologist ? userData.username : null,
      patientUsername: userData?.username && isPsychiatrist ? userData.username : null,
      productCode: productCode,
      groupBy: 'date',
      isParticular
    })

    return res.availabilities
  }

  const { startQueryDateISO, endQueryDateISO } = searchQueryAvailabilitiesDate({
    selectedDay,
    currentFetchDate,
    isPsychologist
  })
  
  const loadProfessionals = ({
    page = 1, perPage = 5, planCode
  }) => {
    const particularPlanCode = 'PLN01000'

    const skills = [actualTheme.codes, actualSubject, actualApproach]
      .flat()
      .filter((skill) => !!skill)

    const planSelectedParam = planSelected
      ? planSelected.partner_plan_code
      : particularPlanCode

    dispatch(
      actions.listProfessionals({
        page,
        per_page: perPage,
        plan_code: planCode ? planCode : planSelectedParam,
        profile_role: config.type,
        status_code: config.statusCode,
        start_date: startQueryDateISO,
        end_date: endQueryDateISO,
        gender: selectedStatus
          ? selectedStatus === 'ALL'
            ? null
            : selectedStatus
          : null,
        skills: skills.length > 0 ? JSON.stringify(skills) : null,
        age_range: actualAgeRanges,
        language: actualLanguages,
        query: actualProfessionalNameTerm,
        ...(actualProfessionalNameTerm
          ? { sort_by: 'friendly_url' }
          : { sort_by: 'score' }
        )
      })
    )
  }

  const isFilterActive = () => {
    return (
      actualTheme ||
      actualSubject ||
      actualApproach ||
      selectedStatus ||
      selectedDay
    )
  }

  const renderProfessionals = (data) => {
    return data.map((professional) => {
      const personInfo = professional.person_general
      const personProfile = professional.person_profile[0]
      const profileDocument = personProfile.documents[0]
      const personDocument = profileDocument ? profileDocument.type_name : ''
      const documentValue = profileDocument ? profileDocument.value : ''
      const personName = personInfo.full_name
      const friendlyUrl = personInfo.friendly_url
      const products = personProfile.products[0]
      const professionalProductCode =
      professional?.person_profile[0]?.products[0]?.product.code
      const professionalProfileRole = getRoleFromProfile(
        personProfile?.profile_code
      )
      const professionalAppointmentId = professional?.person_profile[0]?.products[0]?.appointments[0].id

      const filteredProfessionalSkills = personProfile.skills.filter((skill, index, self) => (
        index === self.findIndex((t) => t.name && t.name === skill.name)
      ))

      return (
        <ProfessionalCard
          key={professional.id}
          documentType={personDocument}
          hasPrice={
            planSelected ? planSelected.partner_plan_is_particular : true
          }
          pictureUrl={personInfo.picture_url}
          professionalId={professional?.person_profile[0]?.id}
          professionalUuid={professional?.uuid}
          professionalAppointmentId={professionalAppointmentId}
          autoReservationEnabled={Boolean(professional?.auto_reservation_enabled) && planGroupAllowsReservation}
          professionalBio={personInfo.bio}
          professionalDoc={documentValue}
          professionalName={personName}
          professionalStatus={'OFFLINE'}
          professionalUrl={friendlyUrl}
          professionalProfileRole={personProfile.profile_code}
          professionalAvailabilities={personInfo.availabilities}
          professionalType={config.type}
          selectedSkill={actualTheme}
          professionalSkills={filteredProfessionalSkills}
          professionalEducations={personProfile.educations}
          professionalHasVideo={!!personInfo?.video_url}
          currentFetchDate={currentFetchDate}
          onShowVideo={() => {
            setSelectedProfessional({
              name: personName,
              doc: `${personDocument} ${documentValue}`,
              video: personInfo.video_url
            })
            setIsShowingVideo(true)
            disableScroll.on()
          }}
          productPrice={products?.appointments[0]?.price}
          productDuration={products?.product?.product_general?.duration}
          onRequestAvailabilities={({ startDate, endDate }) =>
            loadNextAvailabilities({
              startDate: startDate,
              endDate: endDate,
              slug: friendlyUrl,
              profileRole: professionalProfileRole,
              productCode: professionalProductCode,
            })
          }
          reviews={professional.total_reviews}
          onShowReviewsModal={() => {
            setSelectedProfessional({
              name: personName,
              doc: `${personDocument} ${documentValue}`,
              pictureUrl: personInfo.picture_url,
              uuid: professional.uuid,
              rating: professional.total_reviews,
            })
            setReviewsModal(true)
          }}
          patientAlreadyHasReservation={patientAlreadyHasReservation}
          setPatientAlreadyHasReservation={setPatientAlreadyHasReservation}
          patientInfo={{
            creditsValidDate: validDate,
            patientSchedules: patientSchedulesForDayPicker,
            isAuth,
            psychiatryCycleStatus: psychiatryEligibility?.cycleStatus,
          }}
        />
      )
    })
  }

  const handleSelectReviewOption = async (optionId) => {
    setLoadingReviews(true)
    setReviews([])

    const reviewOption = reviewsOptions.find((option) => option.id === optionId)
    setSelectedReviewOption(reviewOption)

    const { reviews, pagination } = await ProfessionalsService.listProfessionalReviews({
      professionalUUID: selectedProfessional.uuid,
      viewAll: optionId === 1
        ? false
        : true
    })

    setReviews(reviews)
    setPageReviews(pagination.page)
    setTotalReviews(pagination.total)

    setLoadingReviews(false)
  }

  const loadMoreReviews = async () => {
    const { reviews, pagination } = await ProfessionalsService.listProfessionalReviews({
      professionalUUID: selectedProfessional.uuid,
      page: pageReviews + 1,
      viewAll: selectedReviewOption.id === 1
        ? false
        : true
    })

    setReviews(prevState => [
      ...prevState,
      ...reviews,
    ])
    setPageReviews(pagination.page)
    setTotalReviews(pagination.total)
  }

  const handleUpdateSelectedPlan = async (planCode) => {
    localStorage.setItem(PATIENT_SAVED_PLAN, planCode)
    
    const newPlan = patientPlans.find(pPlan => pPlan.partner_plan_code === planCode)
    dispatch(actions.authChangePlan(newPlan))
    dispatch({ type: AUTH_UPDATE_DEFAULT_PLAN, payload: { planCode } })
    await updatePatientDefaultPlan({
      planCode,
      username: userData.username
    })
    onChangeFilterCriteria(planCode)
  }

  useEffect(() => {
    if (!reviewsModal) return

    setLoadingReviews(true)

    async function getReviews() {
      const { reviews, pagination } = await ProfessionalsService.listProfessionalReviews({
        professionalUUID: selectedProfessional.uuid,
      })

      setReviews(reviews)
      setPageReviews(pagination.page)
      setTotalReviews(pagination.total)
      setLoadingReviews(false)
    }

    getReviews()

    //eslint-disable-next-line
  }, [reviewsModal])

  const documentTitle = isPsychologist
    ? 'Encontre um Psicólogo online - Telavita'
    : 'Agendamento em Psiquiatria online - Telavita'

  const headerTitle = isPsychologist
    ? 'Encontre um Psicólogo online'
    : 'Agendamento em Psiquiatria online'

  useEffect(() => {
    dispatch(homepageSkillsRequest())

    if (isOnboarding && !isMobile) {
      handleScrollTop()
      disableScroll.on()
    }

    if (!loading && persons.length <= 0 && (!isOnboarding || isMobile)) {
      loadProfessionals({
        page: 1,
        perPage: 5
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }
    onChangeFilterCriteria()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStatus,
    selectedDay,
    actualTheme,
    actualApproach,
    actualProfessionalNameTerm,
    actualAgeRanges,
    actualLanguages,
    isOnboarding
  ])

  useEffect(() => {
    trackFilterEvent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons])

  useEffect(() => {
    if (isAuth && isLoggedUserPatient) {
      updateProfileSelectedPlansLocally()
    }
  }, [])

  const {
    ENTERPRISE,
    CARE_PLUS,
    CORPORATE_LIMITED,
    CORPORATE_UNLIMITED,
    CAPITATION
  } = PlanGroupCategoryEnum

  const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(
    planGroupCategory
  )
  const isEnterprise = useSelector((state) =>
    [ENTERPRISE, CARE_PLUS, CAPITATION].includes(
      state.auth.planSelected?.plan_group.category
    )
  )
  const hideCreditsPanel = !(isAuth && (isCorporate || isEnterprise))
  const showPsychiatry = useEnablePsychiatry()
  const creditsPanel = useSelector((state) => state.creditsPanel.data)
  const availableCredits = creditsPanel.available_credits

  const handleChangeMonthInCalendar = (date) => {
    setSelectedMonth(date)
  }

  const renderCreditsPanel = () => {
    if (!hideCreditsPanel && isPsychiatrist && !showPsychiatry) return

    if (hideCreditsPanel) return

    return (
      <CreditsPanel
        isEnterprise={isEnterprise}
        isCorporate={isCorporate}
        isPsiqProfile={isPsychiatrist}
        professionalProfileRole={config.type}
        isProfessionalProfile={isPsychologist || isPsychiatrist}
        isCareplus={isCareplus}
        isCassi={isCassi}
        isCapitation={isCapitation}
        hasButton={hasButton}
        setHasButton={setHasButton}
        planGroupName={planGroupName}
        creditsStatus={creditsStatus}
        availableCredits={availableCredits}
        maxCredits={maxCredits}
        enableDetails={enableDetails}
        validDate={validDate}
        expired={expired}
        errorButton={errorButton}
        loadingButton={loadingButton}
        solicitationDate={solicitationDate}
        isUnlimited={isUnlimited}
        startDate={startDate}
        corporateEligibility={corporateEligibility}
        loading={loadingEligibility}
        forceUpdate={forceUpdate}
        onClickRequestCreditsButton={onClickRequestCreditsButton}
        psychiatryEligibility={psychiatryEligibility}
        psychiatryEligibilityLoading={psychiatryEligibilityLoading}
        onRequestTreatement={handleRequestTreatementStatus}
        planGroupCode={planGroupCode}
        reasonCode={reasonCode}
        planCode={planSelected?.partner_plan_code}
        creditsPanelErrorMessage={creditsPanelErrorMessage}
        username={userData?.username}
        patientPlans={patientPlans}
      />
    )
  }

  return (
    <>
      <Helmet>
        <meta name="description" content="A Telavita é a única plataforma de terapia online que oferece um cuidado completo com a sua saúde mental. Psicologia online e psiquiatria online é só aqui!" />
        <meta name="robots" content="follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href={'https://app.telavita.com.br'} />
        <title>{documentTitle}</title>
      </Helmet>

      {!isMobile && isOnboarding && (
        <Joyride
          callback={handleJoyrideCallback}
          steps={steps}
          continuous
          disableScrolling
          tooltipComponent={Tooltip}
          styles={{
            options: {
              arrowColor: 'transparent',
              overlayColor: 'rgba(0, 0, 0, 0.75)',
            },
          }}
        />
      )}
      <div className='Homepage'>
        <div className='Homepage__Header'>
          <div className='Homepage__Header__Info'>
            <Typography
              align='mcenter-dleft'
              weight='bold'
              variant='header1'
              customClassName='Homepage__Title__Text'
            >
              {headerTitle}
            </Typography>
            {isAuth && !isMobile && (
              <ProfessionalsNetwork
                planGroupCategory={planGroupCategory}
                planGroupName={planGroupName}
                planName={planName}
                patientPlans={patientPlans}
                selectedPlanCode={planSelected?.partner_plan_code}
                onUpdatePlan={handleUpdateSelectedPlan}
              />
            )}
          </div>
        </div>
        <div className='Homepage__FiltersWrapper'>
          <FilterHeader
            skills={[{ name: 'Todos os temas', code: '' }, ...skills]}
            isPsiq={config.type === 'PSIQ'}
            selectedDay={currentFetchDate}
            approaches={approaches}
            onSelectDate={(date) => dispatch(actions.selectDay(date))}
            onSelectSkill={(skill) =>
              dispatch(filterHomepageActions.setActualTheme(skill))
            }
            onSelectGender={(gender) => dispatch(actions.genderStatus(gender))}
            selectedSkill={actualTheme?.name}
            selectedStatus={selectedStatus}
            openApproachesModal={() => setApproachesModal(true)}
            moreFiltersCounter={onCounterMoreFilters()}
            searchInfo={{
              startDate: startQueryDateISO,
              endDate: endQueryDateISO,
              profileRole: config.type,
              patientSchedulesForDayPicker,
              isLoadingPatientSchedules,
              handleChangeMonthInCalendar
            }}
          />
        </div>
        <Container className='min-height'>
          <Row justify='center'>
            <Column colWidth={10}>
              {isAuth && isMobile && (
                <ProfessionalsNetwork
                  planGroupCategory={planGroupCategory}
                  planGroupName={planGroupName}
                  planName={planName}
                  patientPlans={patientPlans}
                  selectedPlanCode={planSelected?.partner_plan_code}
                  onUpdatePlan={handleUpdateSelectedPlan}
                />
              )}
              <div className='Homepage__timestampContainer'>
                <TimestampTag />
              </div>
              {showMatchProfessionalFeature && (
                <div className='Homepage__professionalMatchContainer Homepage__professionalMatchContainer__top'>
                  <div className='Homepage__professionalMatchContainer--icon'>
                    <MatchShowcaseIcon width={isMatchProfessional && 130}/>
                  </div>
                  <div className='Homepage__professionalMatchContainer--title Homepage__professionalMatchContainer__top--title'>
                    {isMatchProfessional
                      ? (
                        <Typography
                          variant='header4'
                          weight='bold'
                        >
                          {persons.length > 0
                            ? 'Confira abaixo o resultado com os melhores psicólogos para o seu perfil'
                            : 'Não encontramos nenhum profissional ideal para você :('
                          }
                        </Typography>
                      )
                      : (
                        <>
                        <Typography
                          variant='header3'
                          weight='bold'
                        >
                          Quer ajuda para encontrar?
                        </Typography>
                        <Typography
                          variant='content2'
                        >
                          Responda algumas perguntas e <strong>descubra o seu psicólogo ideal.</strong>
                        </Typography>
                        </>
                      )
                    }
                  </div>
                  <div className='Homepage__professionalMatchContainer--button'>
                    <Button
                      variant='contained'
                      uppercase={false}
                      customClassName={isMatchProfessional ? 'light' : 'regular'}
                      onClick={() => setMatchProfessionalsModal(true)}
                    >
                      {isMatchProfessional ? 'Refazer' : 'Descobrir'}
                    </Button>
                  </div>
                </div>
              )}
              <InfiniteScroll
                dataLength={persons.length}
                scrollThreshold={0.6}
                hasMore={
                  loading ||
                  (!professionalsNotFound &&
                    paginationData.total > persons.length)
                }
                loader={
                  <div className='Homepage__professionalsLoading'>
                    <ProfessionalCardLoading />
                    <ProfessionalCardLoading />
                    <ProfessionalCardLoading />
                  </div>
                }
                next={() => {
                  if (!firstUpdate.current && paginationData.page && !loading)
                    loadProfessionals({
                      page: paginationData.page + 1
                    })
                }}
                style={{ overflow: 'visible' }}
              >
                <QuickScheduleProvider>
                  <Row justify='center'>{renderProfessionals(persons)}</Row>
                </QuickScheduleProvider>
              </InfiniteScroll>
              {isOnboarding && !isMobile && (
                <ProfessionalCard
                  documentType={'CRP'}
                  hasPrice
                  pictureUrl={ProfFakeImage}
                  professionalBio="Sou psicóloga clínica e atuo principalmente com a abordagem cognitiva comportamental. Possuo 20 anos de experiência atendendo pacientes de todas as idades, de crianças a idosos, e nos últimos 2 anos com foco em psicoterapia online. Me formei pela universidade federal do Ceará em 2001, com especialização em Filosofia e Psicanálise, e Psicopedagogia Clínica/Institucional."
                  professionalDoc={'00/1234'}
                  professionalName={'Manoela Figueira Muniz'}
                  professionalStatus={'ONLINE'}
                  professionalUrl={'manoela-figueira'}
                  professionalType={'PSIC'}
                  currentFetchDate={currentFetchDate}
                  professionalSkills={[
                    {
                      name: 'Ansiedade',
                    },
                  ]}
                  professionalEducations={[
                    {
                      course_name: 'Mestrado em Psicologia Clínica',
                      institution: 'Universidade de Brasília',
                      start_year: 2012,
                      end_year: 2016,
                    },
                  ]}
                  productPrice={120}
                  productDuration={50}
                  onRequestAvailabilities={({ startDate, endDate }) =>
                    createAvailabilities({ startDate, endDate })
                  }
                />
              )}
              {!loading &&
                !persons.length &&
                !isFilterActive() &&
                (isMobile || !isOnboarding) &&
                !isMatchProfessional && (
                <ResultNotFound
                  iconName='GroupPlanBuilding'
                  iconHeight='40px'
                  iconWidth='40px'
                  title='O convênio selecionado não atende essa especialidade.'
                  text='Modifique a rede de profissionais no filtro acima.'
                />
              )}

              {!loading && persons.length === 0 && isFilterActive() && (isMobile || !isOnboarding) && (
                <ResultNotFound
                  title='Nenhum resultado para esta busca'
                  text='Selecione outra data ou critérios'
                />
              )}
            </Column>
          </Row>
        </Container>

        {!loading && persons.length > 0 && (isMobile || !isOnboarding) && !isMatchProfessional && (
          <div className='Homepage__bottom'>
            <div className='Homepage__bottomMessage'>
              <div onClick={() => handleScrollTop()}>
                <ResultNotFound
                  title='Não encontrou o que esperava?'
                  text='Edite os filtros novamente no topo da tela :)'
                />
              </div>
            </div>
          </div>
        )}

        {showMatchProfessionalFeature && isMatchProfessional && persons.length > 0 && (
          <div className='Homepage__professionalMatchContainer Homepage__professionalMatchContainer__footer'>
            <div className='Homepage__professionalMatchContainer--icon'>
              <MatchShowcaseIcon width={130} />
            </div>
            <div className='Homepage__professionalMatchContainer--title Homepage__professionalMatchContainer__footer--title'>
              <Typography
                variant='header4'
              >
                Não se identificou com nenhum psicólogo?
              </Typography>
            </div>
            <div className='Homepage__professionalMatchContainer--button'>
              <Button
                variant='contained'
                uppercase={false}
                customClassName='light'
                onClick={() => setMatchProfessionalsModal(true)}
              >
                Refazer
              </Button>
            </div>
            <div className='Homepage__professionalMatchContainer--resetButton'>
              <Button
                variant='text'
                uppercase={false}
                customClassName='light'
                onClick={() => {
                  dispatch({
                    type: CLEAR_PERSONS,
                  })
                  resetMatchProfessional()
                  loadProfessionals({
                    page: 1,
                    perPage: 5,
                  })
                }}
              >
                Ver todos os psicólogos
              </Button>
            </div>
          </div>
        )}

        {showMatchProfessionalFeature && isMatchProfessional && persons.length === 0 && (
          <div className='Homepage__professionalMatchContainer Homepage__professionalMatchContainer__footer'>
            <div className='Homepage__professionalMatchContainer--resetButton'>
              <Button
                variant='text'
                uppercase={false}
                customClassName='light'
                onClick={() => {
                  dispatch({
                    type: CLEAR_PERSONS,
                  })
                  resetMatchProfessional()
                  loadProfessionals({
                    page: 1,
                    perPage: 5,
                  })
                }}
              >
                Ver todos os psicólogos
              </Button>
            </div>
          </div>
        )}

        <Footer isMobile={isMobile} pageType={config.type} />
      </div>

      {!isAuth && (
        <SignInBar
          handleLogin={(authData) =>
            dispatch(actions.authLogin(authData, null))
          }
          failMessage={failMessage}
        />
      )}

      {approachesModal && (
        <TherapyApproachModal onClose={() => setApproachesModal(false)} />
      )}

      {matchProfessionalsModal && (
        <MatchProfessionalsModal
          onClose={() => setMatchProfessionalsModal(false)}
          planGroupName={planGroupName ?? 'Particular'}
          matchProfessionalHandled={matchProfessionalHandled}
        />
      )}

      {reviewsModal && (
        <ModalGeneric
          modalTitle={
            <>
              Avaliação dos pacientes<br />
              <Typography
                center
                variant='content2'
                inlineStyles={{
                  color: '#F4A82D',
                  fontSize: isMobile && '14px',
                }}
              >
                <strong>{`${selectedProfessional.rating} ${selectedProfessional.rating === 1 ? 'avaliação' : 'avaliações'}`}</strong> com nota máxima
              </Typography>
            </>
          }
          onCloseModal={() => {
            setReviews([])
            setReviewsModal(false)
            setSelectedReviewOption(() => reviewsOptions.find(
              (review) => review.id === 1
            ))
          }}
          customClassName='Homepage__ReviewsModal'
          containerCustomClassName='Homepage__ReviewsModalContainer'
          topbarCustomClassName='Homepage__ReviewsModalTopbar'
          titleCustomClassName='Homepage__ReviewsModalTitle'
        >
          <div className='Homepage__ReviewsModal__Sidebar'>
            <div className='Homepage__ReviewsModal__Sidebar__ProfessionalDetail'>
              <Avatar
                src={selectedProfessional.pictureUrl}
                alt={selectedProfessional.name}
                title={selectedProfessional.name}
                inlineStyles={{
                  width: isMobile ? 60 : 150,
                  height: isMobile ? 60 : 150,
                }}
              />
              <div className='Homepage__ReviewsModal__Sidebar__ProfessionalDetail__Texts'>
                <Typography
                  weight='bold'
                  inlineStyles={{
                    fontSize: isMobile ? '15px' : '21px',
                    lineHeight: isMobile ? '16px' : '22px',
                    padding: !isMobile && '15px 0 5px',
                    color: '#2D2D2D',
                  }}
                >
                  {selectedProfessional.name}
                </Typography>
                <Typography
                  variant={isMobile ? 'content3' : 'content2'}
                  inlineStyles={{
                    paddingBottom: !isMobile && '20px',
                    color: '#2D2D2D',
                  }}
                >
                  {selectedProfessional.doc}
                </Typography>
              </div>
            </div>
            <Typography
              italic
              variant='content3'
              center={isMobile}
              inlineStyles={{
                color: '#898989',
                maxWidth: isMobile ? 'initial' : '135px',
                lineHeight: '14px',
                padding: isMobile && '0 20px',
              }}
            >
              *Abreviamos os nomes para preservar a privacidade dos nossos pacientes.
            </Typography>
          </div>
          <div className='Homepage__ReviewsModal__Container'>
            <div className='Homepage__ReviewsModal__DescriptionContainer'>
              <Select
                customClassName='Homepage__ReviewsModal__DescriptionContainer__Select'
                label=''
                options={reviewsOptions}
                onSelect={(reviewOption) => handleSelectReviewOption(reviewOption.id)}
                selected={selectedReviewOption}
              />

              {(!reviews.length && !loadingReviews) && (
                <div className='Homepage__ReviewsModal__DescriptionContainer__EmptyReviews'>
                  <BallonCommentIcon
                    fill='#c1c1c1'
                    height='52'
                    width='52'
                    viewBox='0 0 52 52'
                  />
                  <Typography
                    variant='content2'
                    inlineStyles={{ color: '#c1c1c1' }}
                  >
                    Nenhum depoimento recebido
                  </Typography>
                </div>
              )}

              <InfiniteScroll
                dataLength={reviews.length}
                hasMore={(totalReviews > reviews.length || loadingReviews)}
                scrollableTarget='ModalGeneric__Modal'
                loader={
                  <>
                    <ReviewLoading hasActions={false} isMobile={true} />
                    <ReviewLoading hasActions={false} isMobile={true} />
                    <ReviewLoading hasActions={false} isMobile={true} />
                    <ReviewLoading hasActions={false} isMobile={true} />
                    <ReviewLoading hasActions={false} isMobile={true} />
                  </>
                }
                next={loadMoreReviews}
              >
                <ListContainer>
                  {
                    reviews.map((review) => (
                      <Review
                        key={review.id}
                        rating
                        reviewId={review.id}
                        author={review.author}
                        createdAt={format(review.createdAt, 'LLLL yyyy', { locale: pt })}
                        review={review.review}
                      />
                    ))
                  }
                </ListContainer>
              </InfiniteScroll>
            </div>
          </div>
        </ModalGeneric>
      )}
      {isShowingVideo && (
        <div className='Homepage__VideoOverlay'>
          <div className='Homepage__VideoOverlay__Wrapper'>
            <div className='Homepage__VideoOverlay__TextWrapper'>
              <Typography weight='bolder' variant='header2' customClassName='Homepage__VideoOverlay__Text Homepage__VideoOverlay__ProfessionalName'>{selectedProfessional.name}</Typography>
              <Typography customClassName='Homepage__VideoOverlay__Text' variant='content2'>{config.title} - {selectedProfessional.doc}</Typography>
              <button
                className='Homepage__VideoOverlay__CloseButton'
                onClick={() => {
                  setIsShowingVideo(false)
                  disableScroll.off()
                }}
              >
                <CloseModalIcon />
              </button>
            </div>
            <div className='Homepage__VideoOverlay__VideoWrapper'>
              <ReactPlayer
                controls
                className='react-player'
                url={selectedProfessional.video}
                width='100%'
                height='100%'
                playing={true}
              />
            </div>
          </div>
        </div>
      )}
      {renderCreditsPanel()}

      {isAuth && patientAlreadyHasReservation && (
        <BlockScheduleModal
          buttonText='Ir para a minha página inicial'
          onClose={() => history.push(routes.DASHBOARD_HOME_PATIENT)}
          buttonAction={() => history.push(routes.DASHBOARD_HOME_PATIENT)}
          hasCloseButton={false}
        />
      )}
    </>
  )
}

export default Homepage
